import { z } from 'zod'

const bytes = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
export const ProcessVitalsSchema = z.object({
  pid: z.number().int().positive(),
  parentPid: z.number().int().nonnegative(),
  role: z.enum(['sampler', 'driver', 'browser', 'recorder', 'harness', 'runtime', 'other']),
  rssBytes: bytes,
  browserProcessType: z.enum(['browser', 'renderer', 'gpu-process', 'utility', 'zygote', 'other']).optional(),
  rssAnonBytes: bytes.nullable().optional(),
  rssFileBytes: bytes.nullable().optional(),
  rssShmemBytes: bytes.nullable().optional(),
})
const diagnosticStatus = z.enum(['ok', 'unavailable', 'timeout', 'error'])
export const BrowserTargetVitalsSchema = z.object({
  targetId: z.string().max(128),
  type: z.enum(['page', 'iframe', 'worker', 'shared_worker', 'service_worker']),
  heapStatus: diagnosticStatus,
  heapUsedBytes: bytes.nullable(), heapTotalBytes: bytes.nullable(),
  backingStorageBytes: bytes.nullable(), embedderHeapUsedBytes: bytes.nullable(),
  domStatus: diagnosticStatus,
  documents: bytes.nullable(), nodes: bytes.nullable(), listeners: bytes.nullable(),
})
export const BrowserVitalsSchema = z.object({
  browserPid: z.number().int().positive(),
  sampledAt: z.string().datetime(), durationMs: bytes,
  status: diagnosticStatus,
  targetCount: bytes.nullable(),
  targets: z.array(BrowserTargetVitalsSchema).max(12),
})
export const RecorderVitalsSchema = z.object({
  pid: z.number().int().positive(), observedAt: z.string().datetime(),
  queuedRecordingBytes: bytes, checkpoints: bytes, paints: bytes,
})
export const TaskVitalsSchema = z.object({
  sampledAt: z.string().datetime(),
  podName: z.string().min(1).max(253),
  nodeName: z.string().max(253).optional(), podUid: z.string().max(128).optional(),
  memory: z.object({
    usageBytes: bytes.nullable(),
    limitBytes: bytes.nullable(),
    oomKills: bytes.nullable(),
  }),
  processCount: z.number().int().nonnegative(),
  processes: z.array(ProcessVitalsSchema).max(32),
  recordings: z.array(z.object({ id: z.string().uuid(),
    kind: z.enum(['createRecording', 'writeStarted', 'writeFinished', 'crashed']),
    timestamp: bytes.optional(), buildId: z.string().max(256).optional() })).max(32).optional(),
  delivery: z.object({ sequence: bytes, failures: bytes,
    recentFailures: z.array(z.object({ at: z.string().datetime(), status: z.number().int().nullable(),
      error: z.string().max(128).nullable() })).max(8) }).optional(),
  diagnostics: z.object({
    durationMs: bytes,
    browserCount: bytes,
    browsers: z.array(BrowserVitalsSchema).max(4),
    recorderStatus: z.enum(['ok', 'unavailable', 'error']),
    recorders: z.array(RecorderVitalsSchema).max(32),
  }).optional(),
})
export type TaskVitals = z.infer<typeof TaskVitalsSchema>
export type ProcessVitals = z.infer<typeof ProcessVitalsSchema>

export const StoredTaskVitalsSchema = TaskVitalsSchema.extend({
  taskId: z.string(), taskKind: z.string().nullable(), phase: z.string(),
})
